
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Search, 
  Upload, 
  Edit, 
  Trash2, 
  RefreshCw, 
  ImageIcon,
  Info
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isBrowser } from "@/utils/runtime";

interface SiteImage {
  id: string;
  name: string;
  file_path: string;
  alt_text: string | null;
  location: string | null;
  dimensions: string | null;
  size: number | null;
  created_at: string | null;
}

const ImagesModule: React.FC = () => {
  const [images, setImages] = useState<SiteImage[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageDetails, setImageDetails] = useState<SiteImage | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Load images from Supabase
  const loadImages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('site_images')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setImages(data || []);
    } catch (error) {
      console.error("Error loading images:", error);
      toast.error("Erro ao carregar imagens");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  // Filter images based on search term
  const filteredImages = images.filter(image => {
    return (
      image.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (image.location && image.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (image.alt_text && image.alt_text.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  // Handle image deletion
  const handleDeleteImage = async (id: string) => {
    if (isBrowser && globalThis.confirm("Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.")) {
      try {
        setIsDeleting(id);
        
        // First get the image to find the file path
        const { data: imageData, error: fetchError } = await supabase
          .from('site_images')
          .select('file_path')
          .eq('id', id)
          .single();
        
        if (fetchError) {
          throw fetchError;
        }
        
        // Delete from storage if file path exists
        if (imageData && imageData.file_path) {
          const storagePath = imageData.file_path.replace('/storage/v1/object/public/', '');
          const { error: storageError } = await supabase.storage
            .from('images')
            .remove([storagePath]);
          
          if (storageError) {
            console.error("Error removing image from storage:", storageError);
          }
        }
        
        // Delete record from database
        const { error } = await supabase
          .from('site_images')
          .delete()
          .eq('id', id);
          
        if (error) {
          throw error;
        }
        
        toast.success("Imagem excluída com sucesso");
        loadImages();
      } catch (error) {
        console.error("Error deleting image:", error);
        toast.error("Erro ao excluir a imagem");
      } finally {
        setIsDeleting(null);
      }
    }
  };

  // Handle file selection for upload
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Handle image upload
  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo para upload");
      return;
    }

    try {
      setIsUploading(true);
      
      // Generate unique file name
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}-${Date.now()}.${fileExt}`;
      const filePath = `public/${fileName}`;
      
      // Upload to Storage
      const { error: uploadError, data } = await supabase.storage
        .from('images')
        .upload(filePath, selectedFile);
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);
      
      // Create record in site_images table
      const { error: insertError } = await supabase
        .from('site_images')
        .insert({
          name: selectedFile.name.split('.')[0],
          file_path: publicUrl,
          size: selectedFile.size,
          dimensions: await getImageDimensions(selectedFile),
          location: 'upload',
          alt_text: selectedFile.name.split('.')[0],
        });
      
      if (insertError) {
        throw insertError;
      }
      
      toast.success("Imagem carregada com sucesso");
      setSelectedFile(null);
      loadImages();
      
      // Reset file input
      const fileInput = globalThis.document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Erro ao fazer upload da imagem");
    } finally {
      setIsUploading(false);
    }
  };

  // Get image dimensions (helper function)
  const getImageDimensions = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function() {
        const dimensions = `${img.width}x${img.height}`;
        URL.revokeObjectURL(img.src);
        resolve(dimensions);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Show image details
  const handleViewDetails = (image: SiteImage) => {
    setImageDetails(image);
    setShowDetailsModal(true);
  };
  
  // Format file size
  const formatFileSize = (size: number | null) => {
    if (!size) return 'Unknown';
    
    if (size < 1024) {
      return `${size} bytes`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(2)} KB`;
    } else {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-toctoc-blue"></div>
        <span className="ml-3">Carregando imagens...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Gerenciamento de Imagens</h1>
          <p className="text-gray-500">Upload, edição e organização das imagens do site</p>
        </div>
        <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-4">
          <div className="flex items-center">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/*"
              onChange={handleFileSelect}
            />
            <label htmlFor="file-upload" className="cursor-pointer bg-gray-100 text-gray-700 px-4 py-2 rounded-l flex items-center">
              <Upload className="w-4 h-4 mr-2" />
              Escolher
            </label>
            <span className="bg-gray-50 px-3 py-2 border border-l-0 border-gray-200 rounded-r text-sm truncate max-w-[150px]">
              {selectedFile?.name || "Nenhum arquivo"}
            </span>
          </div>
          <Button 
            onClick={handleUploadImage} 
            className="bg-toctoc-blue hover:bg-blue-700"
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Enviar Imagem
              </>
            )}
          </Button>
          <Button 
            variant="outline"
            onClick={loadImages}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="flex items-center mb-6 bg-white rounded-lg shadow p-2">
        <Search className="w-5 h-5 text-gray-400 ml-2" />
        <Input
          className="border-0 focus-visible:ring-0"
          placeholder="Buscar por nome, localização ou descrição..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {/* Images Grid */}
      {filteredImages.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <ImageIcon className="w-16 h-16 mx-auto text-gray-300" />
          <p className="text-gray-500 mt-4">
            {searchTerm ? "Nenhuma imagem encontrada com esse termo de busca" : "Nenhuma imagem disponível"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredImages.map(image => (
            <Card key={image.id} className="overflow-hidden">
              <div className="aspect-ratio-1/1 h-48 bg-gray-50 relative group">
                <img 
                  src={image.file_path} 
                  alt={image.alt_text || image.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://via.placeholder.com/400x400?text=Imagem+não+encontrada";
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="bg-white"
                    onClick={() => handleViewDetails(image)}
                  >
                    <Info className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={isDeleting === image.id}
                  >
                    {isDeleting === image.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {image.location && (
                  <span className="absolute top-2 right-2 bg-gray-800 text-white px-2 py-1 text-xs rounded">
                    {image.location}
                  </span>
                )}
              </div>
              <CardContent className="p-3">
                <h3 className="font-medium text-sm truncate" title={image.name}>
                  {image.name}
                </h3>
                <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                  <span title={image.dimensions || undefined}>{image.dimensions || 'Dim. desconhecida'}</span>
                  <span>{formatFileSize(image.size)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Image Details Modal */}
      {showDetailsModal && imageDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Detalhes da imagem</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <img 
                    src={imageDetails.file_path} 
                    alt={imageDetails.alt_text || imageDetails.name} 
                    className="w-full h-auto object-contain max-h-80"
                  />
                </div>
                <div>
                  <div className="grid grid-cols-[120px_1fr] gap-2">
                    <div className="text-gray-600 font-medium">Nome:</div>
                    <div>{imageDetails.name}</div>
                    
                    <div className="text-gray-600 font-medium">Texto alternativo:</div>
                    <div>{imageDetails.alt_text || '-'}</div>
                    
                    <div className="text-gray-600 font-medium">Localização:</div>
                    <div>{imageDetails.location || '-'}</div>
                    
                    <div className="text-gray-600 font-medium">Dimensões:</div>
                    <div>{imageDetails.dimensions || 'Desconhecido'}</div>
                    
                    <div className="text-gray-600 font-medium">Tamanho:</div>
                    <div>{formatFileSize(imageDetails.size)}</div>
                    
                    <div className="text-gray-600 font-medium">URL:</div>
                    <div className="text-xs break-all">
                      <a 
                        href={imageDetails.file_path} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {imageDetails.file_path}
                      </a>
                    </div>
                    
                    <div className="text-gray-600 font-medium">Data de upload:</div>
                    <div>
                      {imageDetails.created_at 
                        ? new Date(imageDetails.created_at).toLocaleDateString('pt-BR') 
                        : 'Desconhecido'
                      }
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <Input
                      placeholder="Nome da imagem"
                      value={imageDetails.name}
                      onChange={e => setImageDetails({...imageDetails, name: e.target.value})}
                    />
                    <Input
                      placeholder="Texto alternativo"
                      value={imageDetails.alt_text || ''}
                      onChange={e => setImageDetails({...imageDetails, alt_text: e.target.value})}
                    />
                    <Input
                      placeholder="Localização (ex: hero, footer)"
                      value={imageDetails.location || ''}
                      onChange={e => setImageDetails({...imageDetails, location: e.target.value})}
                    />
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      className="bg-toctoc-blue hover:bg-blue-700"
                      onClick={async () => {
                        try {
                          const { error } = await supabase
                            .from('site_images')
                            .update({
                              name: imageDetails.name,
                              alt_text: imageDetails.alt_text,
                              location: imageDetails.location
                            })
                            .eq('id', imageDetails.id);
                            
                          if (error) throw error;
                          
                          toast.success("Detalhes da imagem atualizados");
                          loadImages();
                          setShowDetailsModal(false);
                        } catch (error) {
                          console.error("Error updating image details:", error);
                          toast.error("Erro ao atualizar detalhes da imagem");
                        }
                      }}
                    >
                      Salvar Alterações
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesModule;
